/**
 * Maps a live playlist's media timeline onto how long the stream has been live.
 *
 * A live playlist's timeline is only as long as what the viewer can see of it:
 * hls.js starts it at the first playlist it loads, so without DVR it is a
 * rolling window of about thirty seconds and a viewer who joins a stream that
 * has been live for an hour reads "0:24". The server knows when the stream
 * started and sends the uptime with every status poll; this turns that into the
 * number to show for the playhead.
 */

/** The attribute the player sets on a time display to carry the uptime. */
export const LIVE_ELAPSED_ATTR = 'liveelapsed';

/**
 * How much later than the true value a measurement may read before it is taken
 * as a restart of the timeline rather than noise. A measurement reads late by
 * up to a segment, because hls.js only learns of a segment at its next
 * playlist reload; a source reload that restarts the timeline reads late by
 * the whole time the viewer had been watching.
 */
export const LIVE_OFFSET_RESTART_SECONDS = 6;

/**
 * How far the held offset moves toward each later-reading measurement. Slow:
 * it only follows an encoder clock that drifts against the wall clock, and must
 * not follow the noise.
 */
const LIVE_OFFSET_DRIFT_FOLLOW = 0.02;

export class LiveUptimeClock {
  #anchor: { seconds: number; at: number } | null = null;
  #offset: number | null = null;
  #lastSeekableEnd: number | undefined;
  readonly #now: () => number;

  constructor(now: () => number = () => performance.now()) {
    this.#now = now;
  }

  /** Whether the server has said how long the stream has been live. */
  get hasUptime(): boolean {
    return this.#anchor !== null;
  }

  /**
   * Record the stream's uptime as the server reported it just now. A value that
   * is not a usable number drops the mapping, so the caller falls back to the
   * media timeline.
   */
  setUptime(seconds: number | null): void {
    if (seconds === null || !Number.isFinite(seconds) || seconds < 0) {
      this.#anchor = null;
      this.#offset = null;
      this.#lastSeekableEnd = undefined;
      return;
    }
    this.#anchor = { seconds, at: this.#now() };
  }

  /** The stream's uptime at the live edge, right now. */
  edgeUptime(): number | null {
    if (!this.#anchor) return null;
    return this.#anchor.seconds + (this.#now() - this.#anchor.at) / 1000;
  }

  /**
   * Seconds to add to a media time to get stream uptime, or null while there is
   * nothing to measure it against: no uptime yet, or no seekable range, which
   * is also the state of a timeline that has just restarted. The held offset
   * belongs to the old timeline then, so it is not offered.
   *
   * It is measured when the end of the seekable range moves, because that is
   * the one moment the playlist's edge is known to be current; read at any
   * other time it would run up to a segment behind the clock and the counter
   * would saw-tooth. A measurement can only read late (the edge is learned
   * late), so one that reads earlier than the held offset is better and is
   * taken at once; one that reads much later is a restarted timeline; the rest
   * only nudge the offset, to follow drift.
   */
  offsetFor(seekableEnd: number | undefined): number | null {
    const edgeUptime = this.edgeUptime();
    if (edgeUptime === null || seekableEnd === undefined) return null;

    if (this.#offset === null || seekableEnd !== this.#lastSeekableEnd) {
      this.#lastSeekableEnd = seekableEnd;
      const measured = edgeUptime - seekableEnd;
      if (
        this.#offset === null ||
        measured < this.#offset ||
        measured - this.#offset > LIVE_OFFSET_RESTART_SECONDS
      ) {
        this.#offset = measured;
      } else {
        this.#offset += (measured - this.#offset) * LIVE_OFFSET_DRIFT_FOLLOW;
      }
    }
    return this.#offset;
  }
}
